import { AllAccessibleColumns } from '../../../shared/types';
import {
  normalizeLabel,
  parseNumeric,
  resolveOptionLabel,
} from '../../../shared/utils';
import { isAugsburgByWerkValue } from './plantScope';
import {
  DynamicStatusSegmentsParams,
  FacultyConfig,
  NeedField,
  NeedTriggeredParams,
  NotificationCandidate,
  NotificationCandidatesParams,
  Option,
  StatusField,
  StatusFieldParams,
  StatusOptionsByField,
  StatusSnapshot,
} from './downstreamStatusUpdateToAvTypes';

const STATUS_FIELDS: StatusField[] = ['pp_erledigt', 'ncperl', 'cmmerl', 'qsfaerl'];

const FACULTY_CONFIG: FacultyConfig[] = [
  {
    faculty: 'QP',
    needField: 'inspection_planning_need',
    statusField: 'pp_erledigt',
  },
  {
    faculty: 'NCP',
    needField: 'ncpneed',
    statusField: 'ncperl',
  },
  {
    faculty: 'CMM',
    needField: 'cmmneed',
    statusField: 'cmmerl',
  },
  {
    faculty: 'QSFA',
    needField: 'qsfaneed',
    statusField: 'qsfaerl',
  },
];

const YES_LABEL = 'yes';
const INSPECTION_PLANNING_TRIGGER_LABELS = new Set(['full fai', 'delta fai']);

// Checks whether a row id is a valid string or number.
export function isRowId(value: unknown): value is string | number {
  return typeof value === 'string' || typeof value === 'number';
}

// Collects dropdown options for downstream status fields.
export function getStatusOptionsByField(
  allAccessibleColumns: AllAccessibleColumns,
): StatusOptionsByField {
  return {
    pp_erledigt: (allAccessibleColumns.find((col) => col.attrInternalName === 'pp_erledigt')
      ?.valueOptions ?? []) as Option[],
    ncperl: (allAccessibleColumns.find((col) => col.attrInternalName === 'ncperl')?.valueOptions ??
      []) as Option[],
    cmmerl: (allAccessibleColumns.find((col) => col.attrInternalName === 'cmmerl')?.valueOptions ??
      []) as Option[],
    qsfaerl: (allAccessibleColumns.find((col) => col.attrInternalName === 'qsfaerl')?.valueOptions ??
      []) as Option[],
  };
}

// Collects dropdown options for downstream need fields.
export function getNeedOptionsByField(
  allAccessibleColumns: AllAccessibleColumns,
): Record<NeedField, Option[]> {
  return {
    inspection_planning_need: (allAccessibleColumns.find(
      (col) => col.attrInternalName === 'inspection_planning_need',
    )?.valueOptions ?? []) as Option[],
    ncpneed: (allAccessibleColumns.find((col) => col.attrInternalName === 'ncpneed')?.valueOptions ??
      []) as Option[],
    cmmneed: (allAccessibleColumns.find((col) => col.attrInternalName === 'cmmneed')?.valueOptions ??
      []) as Option[],
    qsfaneed: (allAccessibleColumns.find((col) => col.attrInternalName === 'qsfaneed')?.valueOptions ??
      []) as Option[],
  };
}

// Resolves trigger ids for Done/Rejected status labels.
export function getTriggerIds(options: Option[]): Set<number> {
  const triggerLabels = new Set(['done', 'rejected']);
  const ids = options
    .filter((option) => triggerLabels.has(normalizeLabel(option.label)))
    .map((option) => option.value)
    .filter((value) => Number.isFinite(value));

  return new Set(ids);
}

// Resolves a user-facing status label for a raw status value.
export function resolveStatusLabel(value: unknown, options: Option[]): string {
  const parsed = parseNumeric(value);
  if (parsed == null) {
    return '-';
  }

  const label = options.find((option) => option.value === parsed)?.label;
  return typeof label === 'string' && label.trim() ? label : '-';
}

// Determines whether a need is explicitly requested in the current row state.
export function isNeedTriggered({
  needField,
  row,
  originalRow,
  optionsByNeedField,
}: NeedTriggeredParams & { row: Record<string, unknown>; originalRow: Record<string, unknown> }): boolean {
  const currentValue = row[needField] !== undefined ? row[needField] : originalRow[needField];
  const label = resolveOptionLabel(currentValue, optionsByNeedField[needField]);

  if (!label || label === 'no') {
    return false;
  }

  if (needField === 'inspection_planning_need') {
    return INSPECTION_PLANNING_TRIGGER_LABELS.has(label);
  }

  return label === YES_LABEL;
}

// Builds dynamic status segments for requested faculties only.
export function buildDynamicStatusSegments({
  row,
  originalRow,
  optionsByNeedField,
  optionsByStatusField,
}: DynamicStatusSegmentsParams): string {
  const triggered = FACULTY_CONFIG.filter((config) =>
    isNeedTriggered({
      needField: config.needField,
      row,
      originalRow,
      optionsByNeedField,
    }),
  );

  return triggered
    .map((config) => {
      const currentStatusValue =
        row[config.statusField] !== undefined ? row[config.statusField] : originalRow[config.statusField];
      const statusLabel = resolveStatusLabel(currentStatusValue, optionsByStatusField[config.statusField]);
      return `${config.faculty}: ${statusLabel}`;
    })
    .join(', ');
}

// Detects if any downstream status transitioned into a trigger state.
export function hasAnyStatusTransitionedToTrigger({
  row,
  originalRow,
  optionsByField,
}: StatusFieldParams): boolean {
  return STATUS_FIELDS.some((field) => {
    const triggerIds = getTriggerIds(optionsByField[field]);
    if (!triggerIds.size) {
      return false;
    }

    const currentStatus = parseNumeric(row[field]);
    const previousStatus = parseNumeric(originalRow[field]);

    return currentStatus != null && triggerIds.has(currentStatus) && !triggerIds.has(previousStatus ?? -1);
  });
}

// Builds the full current downstream status snapshot for notification payloads.
export function buildCurrentStatusSnapshot({
  row,
  originalRow,
  optionsByField,
}: StatusFieldParams): StatusSnapshot {
  const resolveCurrentValue = (field: StatusField) =>
    row[field] !== undefined ? row[field] : originalRow[field];

  return {
    qp_status: resolveStatusLabel(resolveCurrentValue('pp_erledigt'), optionsByField.pp_erledigt),
    ncp_status: resolveStatusLabel(resolveCurrentValue('ncperl'), optionsByField.ncperl),
    cmm_status: resolveStatusLabel(resolveCurrentValue('cmmerl'), optionsByField.cmmerl),
    qsfa_status: resolveStatusLabel(resolveCurrentValue('qsfaerl'), optionsByField.qsfaerl),
  };
}

// Builds notification candidates from edited rows that satisfy all trigger rules.
export function buildNotificationCandidates({
  editedRow,
  editedRowOriginal,
  optionsByField,
  optionsByNeedField,
  valueOptionsProfitCenter,
  valueOptionsWerk,
}: NotificationCandidatesParams): NotificationCandidate[] {
  const candidates: NotificationCandidate[] = [];

  editedRow.forEach((row) => {
    if (!isRowId(row?.id1)) {
      return;
    }

    const rowIdKey = String(row.id1);
    const originalRow = editedRowOriginal?.[rowIdKey] ?? editedRowOriginal?.[row.id1 as string];

    if (!originalRow) {
      return;
    }

    const currentWerkValue = row.werk ?? null;
    if (!isAugsburgByWerkValue(currentWerkValue, valueOptionsWerk)) {
      return;
    }

    if (!hasAnyStatusTransitionedToTrigger({ row, originalRow, optionsByField })) {
      return;
    }

    const rowId = parseNumeric(row.id1);
    const pcnrValue = row.pf_lc ?? originalRow.pf_lc;
    const pcnrId = parseNumeric(pcnrValue);

    if (rowId == null || pcnrId == null) {
      return;
    }

    const pcnrLabel =
      valueOptionsProfitCenter.find((option) => option.value === pcnrId)?.label ?? String(pcnrId);

    const statusSegments = buildDynamicStatusSegments({
      row,
      originalRow,
      optionsByNeedField,
      optionsByStatusField: optionsByField,
    });

    if (!statusSegments) {
      return;
    }

    candidates.push({
      rowId,
      pcnrId,
      pcnrLabel,
      statuses: buildCurrentStatusSnapshot({ row, originalRow, optionsByField }),
      statusSegments,
    });
  });

  return candidates;
}
